import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import useCallbackRef from 'hooks/useCallbackRef';
import {
  DATA_ROOM_RFI_NOTIFICATION_MEDIUM,
  DATA_ROOM_RFI_STATUS,
  DataRoomRFI,
} from 'router/subrouters/DataRoom/models/DataRoomRfi';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import {
  SideDrawerProps,
  SideDrawerRef,
} from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import { Button } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import RfiStatusTag from '../RfiStatusTag';
import useRfiCategory from 'hooks/useRfiCategory';
import utils, { dateFormat, getCheckedValuesInObject } from 'utils';
import CheckboxFieldGroup from 'components/CheckboxFieldGroup';
import { useRfi } from 'hooks/useRfi';
import ToggleField from 'components/ToggleField';
import useRfiGrantAccess from 'hooks/useRfiGrantAccess';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import RfiAddRecipientsModal from '../RfiAddRecipientsModal';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import RecipientsList from '../RecipientsList';
import useRfiChangeStatus from 'router/subrouters/DataRoom/hooks/useRfiChangeStatus';
import { getIsRfiStatusClosed } from 'router/subrouters/DataRoom/utils';

import './RfiDetailsDrawer.styles.scss';
import './RfiDetailsDrawer.styles.responsive.scss';

type RfiDetailsDrawerProps = {
  rfi: DataRoomRFI;
  onRfiUpdate?: (updatedRfi: DataRoomRFI) => void;
} & SideDrawerProps;

const RfiDetailsDrawer: React.ForwardRefRenderFunction<
  SideDrawerRef,
  RfiDetailsDrawerProps
> = (props, ref) => {
  const { className, rfi, onRfiUpdate, ...rest } = props;

  const {
    _id,
    name,
    status,
    dueDate,
    twentyFourHoursBeforeDeadlineReminder,
    atDeadlineReminder,
    threeDaysAfterDeadlineReminder,
    fiveDaysAfterDeadlineReminder,
    automaticFileUploadToDataRoom,
    notificationMedium = [],
    dataRoomRFICategories = [],
    grantAccessDataRoomMemberIds = [],
    allRecipientsWithReminders = [],
  } = rfi || {};

  const { popSuccess, popServerError } = useContext(PopNotificationsContext);

  const classes = classNames('rfi-details-drawer', className);

  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>();
  const [modal, modalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => drawer, [drawer]);

  const { notificationMediumOptions, getDueDateReminderOptions } = useRfi();
  const { getCategoryName, getRequestedAndAdditionalCategory } =
    useRfiCategory();
  const { membersOptions, membersInitialValue } = useRfiGrantAccess(
    grantAccessDataRoomMemberIds,
  );

  const { requestedDataRoomRFICategory } = getRequestedAndAdditionalCategory(
    dataRoomRFICategories,
  );

  const { t } = useTranslation();

  const { changeStatus, loading } = useRfiChangeStatus(rfi, (data) => {
    onRfiUpdate(data);
    drawer?.setOpen(false);
  });

  const dueDateCopy = useMemo(() => {
    if (!dueDate) return '';

    const { type, count } = utils.humanize(dueDate);

    return t(type, { count });
  }, [dueDate, t]);

  const RenderCheckboxes = useMemo(
    () => getDueDateReminderOptions(!dueDate),
    [dueDate, getDueDateReminderOptions],
  );

  const initialValues = useMemo(
    () => ({
      twentyFourHoursBeforeDeadlineReminder,
      atDeadlineReminder,
      threeDaysAfterDeadlineReminder,
      fiveDaysAfterDeadlineReminder,
      automaticFileUploadToDataRoom,
      members: membersInitialValue,
      notificationMedium: {
        [DATA_ROOM_RFI_NOTIFICATION_MEDIUM.EMAIL]: notificationMedium.includes(
          DATA_ROOM_RFI_NOTIFICATION_MEDIUM.EMAIL,
        ),
        [DATA_ROOM_RFI_NOTIFICATION_MEDIUM.SMS]: notificationMedium.includes(
          DATA_ROOM_RFI_NOTIFICATION_MEDIUM.SMS,
        ),
        [DATA_ROOM_RFI_NOTIFICATION_MEDIUM.PLATFORM]:
          notificationMedium.includes(
            DATA_ROOM_RFI_NOTIFICATION_MEDIUM.PLATFORM,
          ),
      },
    }),
    [
      atDeadlineReminder,
      automaticFileUploadToDataRoom,
      fiveDaysAfterDeadlineReminder,
      membersInitialValue,
      notificationMedium,
      threeDaysAfterDeadlineReminder,
      twentyFourHoursBeforeDeadlineReminder,
    ],
  );

  const onSubmit = useCallback(
    async (values) => {
      const { notificationMedium, members, ...rest } = values;

      const notificationMediumFinal =
        getCheckedValuesInObject(notificationMedium);
      const grantAccessDataRoomMemberIds = getCheckedValuesInObject(members);

      try {
        const { data } = await dataRoomApi.rfi.patchRfi(_id, {
          grantAccessDataRoomMemberIds,
          notifyOnComplete: !!notificationMediumFinal.length,
          notificationMedium: notificationMediumFinal,
          ...rest,
        });

        popSuccess(t('succesfullyUpdatedRFI'));
        onRfiUpdate?.(data);
        drawer?.setOpen(false);
      } catch (e) {
        popServerError(e);
      }
    },
    [_id, drawer, onRfiUpdate, popServerError, popSuccess, t],
  );

  return (
    <SideDrawer ref={drawerRef} className={classes} {...rest}>
      <Form
        keepDirtyOnReinitialize
        initialValues={initialValues}
        onSubmit={onSubmit}
        render={({ handleSubmit, submitting, pristine }) => (
          <form onSubmit={handleSubmit}>
            <div className="heading">
              <p className="drawer-title">{name}</p>
              <RfiStatusTag status={status} />
            </div>

            <div className="subcontent">
              <p className="subtitle">{t('requestedFiles')}</p>

              <ol>
                {requestedDataRoomRFICategory.map(({ _id, name }) => (
                  <li key={_id}>{getCategoryName(name)}</li>
                ))}
              </ol>
            </div>

            {dueDateCopy && (
              <>
                <div className="subcontent">
                  <p className="subtitle">{`${t('dueDate')} ${dateFormat(
                    dueDate,
                  )}`}</p>

                  <p>{`${t('dueDate')} ${dueDateCopy}.`}</p>
                </div>

                <div className="subcontent">
                  <p className="subtitle">{t('dueDateRemindersFrequency')}</p>

                  <div className="options">{RenderCheckboxes}</div>
                </div>
              </>
            )}

            <div className="subcontent">
              <p className="subtitle">{t('notificationMediums')}</p>

              <p>{t('notifyMeAboutRequestUpdates')}</p>

              <div>
                <CheckboxFieldGroup
                  name="notificationMedium"
                  options={notificationMediumOptions}
                />
              </div>
            </div>

            <div className="subcontent">
              <p className="subtitle">{t('autoUpload')}</p>

              <Field
                name="automaticFileUploadToDataRoom"
                component={ToggleField}
                type="checkbox"
                label={t('dataRoomAutoUpload')}
              />

              <p>{t('autoUploadDescription')}</p>
            </div>

            <div className="subcontent">
              <p className="subtitle">{t('grantFileAccess')}</p>

              <p>{t('chooseDataRoomMembersGrantAccess')}</p>

              <div className="grant-access-members">
                <CheckboxFieldGroup
                  options={membersOptions}
                  name="members"
                  direction="column"
                />
              </div>
            </div>

            <div className="subcontent">
              <div className="subcontent__header">
                <p className="subtitle">{t('recipientList')}</p>
                {!getIsRfiStatusClosed(status) && (
                  <Button onClick={modal?.open}>{t('addRecipients')}</Button>
                )}
              </div>

              <RecipientsList
                rfiId={_id}
                status={status}
                allRecipientsWithReminders={allRecipientsWithReminders}
                updateAllRecipientsWithReminders={(
                  allRecipientsWithReminders,
                ) => onRfiUpdate({ ...rfi, allRecipientsWithReminders })}
              />
            </div>

            <div className="actions">
              <Button
                variant="outline"
                disabled={loading}
                onClick={changeStatus}
              >
                {t(
                  status === DATA_ROOM_RFI_STATUS.CLOSED
                    ? 'reopenRequest'
                    : 'closeRequest',
                )}
              </Button>

              <Button type="submit" disabled={pristine || submitting}>
                {t('saveChanges')}
              </Button>
            </div>
          </form>
        )}
      />

      <RfiAddRecipientsModal
        ref={modalRef}
        rfiId={_id}
        onRfiUpdate={onRfiUpdate}
      />
    </SideDrawer>
  );
};

export default forwardRef(RfiDetailsDrawer);
